import { useEffect, useState } from 'react'
import type {
  AbilityName,
  CharacterSheet,
  LevelPlan,
  LevelUpCommandInput,
} from '@rollcall/contracts'
import type { Api } from '../api.js'
import { Description } from './description.js'
import { useT } from './i18n-context.js'
import { featureName } from './labels.js'
import { IncreaseField } from './increase-field.js'
import { SlotField } from './slot-field.js'

/**
 * Повышение уровня (D31).
 *
 * Экран ничего не решает сам: что выбрать, объявил план, а он собран
 * из записи класса — какой уровень даёт подкласс, какой прибавку
 * характеристик, сколько добавится заклинаний. Новый класс появится
 * здесь без правки кода, как и в визарде создания.
 *
 * Хиты за уровень — выбор игрока (с. 23): фиксированное значение
 * из записи класса или бросок. Бросок делает сервер (D12), и он же
 * кладёт его в журнал бросков.
 */

export type LevelUpProps = {
  readonly api: Api
  readonly sheet: CharacterSheet
  readonly busy: boolean
  readonly problems: Readonly<Record<string, readonly string[]>>
  readonly onLevelUp: (input: Omit<LevelUpCommandInput, 'characterId'>) => void
  readonly onLevelDown: () => void
  readonly onClose: () => void
  /**
   * Подпись выхода. На листе это «назад к листу»; в заявке по ссылке
   * листа ещё не видно, и выход там — прервать сборку (D60).
   */
  readonly closeLabel?: string | undefined
}

/**
 * Экран живёт один уровень: вызывающие ставят ему `key={sheet.level}`.
 * План и выборы принадлежат уровню, на который поднимают, — при сборке
 * подряд (D60) экран со старым планом предлагал бы второй раз тот же
 * уровень.
 */
export function LevelUp({
  api,
  sheet,
  busy,
  problems,
  onLevelUp,
  onLevelDown,
  onClose,
  closeLabel,
}: LevelUpProps) {
  const t = useT()
  const [plan, setPlan] = useState<LevelPlan | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [picks, setPicks] = useState<Record<string, readonly string[]>>({})
  const [increase, setIncrease] = useState<
    readonly { ability: AbilityName; amount: number }[]
  >([])
  const [hitPoints, setHitPoints] = useState<'fixed' | 'roll' | 'manual'>('fixed')
  const [manual, setManual] = useState('')

  const characterId = sheet.character.id
  useEffect(() => {
    let cancelled = false
    void api
      .levelPlan(characterId)
      .then((next) => {
        if (!cancelled) setPlan(next)
      })
      .catch(() => {
        // Двадцатый уровень — не поломка: выше него планов нет.
        if (!cancelled) setFailure(t('level.noPlan'))
      })
    return () => {
      cancelled = true
    }
  }, [api, characterId, t])

  if (failure !== null) {
    return (
      <section className="app-screen" aria-labelledby="level-title">
        <h2 id="level-title">{t('level.title')}</h2>
        <p className="app-problems" role="alert">
          {failure}
        </p>
        <Back onClose={onClose} label={closeLabel} />
      </section>
    )
  }

  if (plan === null) {
    return (
      <section className="app-screen" aria-labelledby="level-title">
        <h2 id="level-title">{t('level.title')}</h2>
        <p>{t('characters.loading')}</p>
      </section>
    )
  }

  // Выборы уровня проверяет сервер, но неполные он и не должен
  // видеть: кнопка ждёт, пока выбрано столько, сколько объявил план.
  const ready =
    plan.slots.every((slot) => (picks[slot.key] ?? []).length === slot.choose) &&
    (plan.increase === null || increase.length > 0) &&
    (hitPoints !== 'manual' || Number(manual) >= 1)
  const refusals = Object.values(problems).flat()

  return (
    <section className="app-screen" aria-labelledby="level-title">
      <h2 id="level-title">{t('level.upTo', { level: plan.level })}</h2>

      <section aria-labelledby="level-features">
        <h3 id="level-features">{t('level.features')}</h3>
        {plan.features.length === 0 ? (
          <p>{t('level.noFeatures')}</p>
        ) : (
          <ul className="app-list app-features">
            {plan.features.map((feature) => (
              <li key={feature.key} className="app-feature">
                {/* Имя и текст — из записи класса, то есть английские:
                    переводятся термины, а не описания (D11). */}
                <h4>{featureName(t, feature.key, feature.name)}</h4>
                <Description text={feature.text} textRu={feature.textRu} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="level-hp">
        <h3 id="level-hp">{t('level.hitPoints')}</h3>
        <p className="app-slot-hint">
          {t('level.hitPointsHint', { die: plan.hitDie, fixed: plan.fixedHp })}
        </p>
        <div
          className="app-slot-options"
          role="radiogroup"
          aria-label={t('level.hitPoints')}
        >
          {/* Три пути, и все равноправны (D12): фиксированное значение
              из записи класса, бросок на сервере и число, которое
              игрок выбросил настоящей костью. */}
          {(['fixed', 'roll', 'manual'] as const).map((kind) => (
            <label key={kind} className="app-option">
              <input
                type="radio"
                name="level-hp"
                checked={hitPoints === kind}
                onChange={() => {
                  setHitPoints(kind)
                }}
              />
              <span>
                {kind === 'fixed'
                  ? t('level.hpFixed', { value: plan.fixedHp })
                  : kind === 'roll'
                    ? t('level.hpRoll', { die: plan.hitDie })
                    : t('level.hpManual')}
              </span>
            </label>
          ))}
        </div>

        {hitPoints === 'manual' && (
          <>
            <label className="app-field app-field-narrow">
              <span>{t('level.hpManualField')}</span>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                value={manual}
                onChange={(event) => {
                  setManual(event.target.value)
                }}
              />
            </label>
            <p className="app-slot-hint">
              {t('level.hpManualHint', { die: plan.hitDie })}
            </p>
          </>
        )}
      </section>

      {plan.slots.map((slot) => (
        <SlotField
          key={slot.key}
          slot={slot}
          picked={picks[slot.key] ?? []}
          problems={problems[slot.key] ?? []}
          onChange={(values) => {
            setPicks({ ...picks, [slot.key]: values })
          }}
        />
      ))}

      {plan.increase !== null && (
        <IncreaseField
          plan={plan.increase}
          increase={increase}
          problems={problems['increase'] ?? []}
          onChange={setIncrease}
        />
      )}

      {/* Отказ показывается у кнопки, а не только у слота внизу
          длинного списка: иначе нажатие выглядит как «ничего
          не произошло» — а это и была самая частая жалоба. */}
      {refusals.length > 0 && (
        <ul className="app-problems" role="alert" data-testid="level-problems">
          {refusals.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}
      {!ready && refusals.length === 0 && (
        <p className="app-slot-hint">{t('level.incomplete')}</p>
      )}

      <div className="app-actions">
        <button
          type="button"
          className="app-button"
          disabled={busy || !ready}
          onClick={() => {
            onLevelUp({
              picks: Object.entries(picks).flatMap(([slot, values]) =>
                values.map((value) => ({ slot, value })),
              ),
              increase: [...increase],
              hitPoints:
                hitPoints === 'manual'
                  ? { kind: 'manual', amount: Math.max(1, Math.trunc(Number(manual))) }
                  : { kind: hitPoints },
            })
          }}
        >
          {t('level.confirm', { level: plan.level })}
        </button>
        <Back onClose={onClose} label={closeLabel} />
      </div>

      {/* Откат возможен, пока за повышением ничего не записано (D31):
          дальше это уже не «отменить», а «переписать журнал». */}
      {sheet.level > 1 && (
        <div className="app-actions">
          <button
            type="button"
            className="app-button app-button-quiet"
            disabled={busy}
            onClick={onLevelDown}
          >
            {t('level.undo', { level: sheet.level })}
          </button>
        </div>
      )}
    </section>
  )
}

function Back({
  onClose,
  label,
}: {
  readonly onClose: () => void
  readonly label?: string | undefined
}) {
  const t = useT()
  return (
    <button type="button" className="app-button app-button-quiet" onClick={onClose}>
      {label ?? t('level.back')}
    </button>
  )
}
